import { Text as VText } from "@visx/text";
import type { TextProps } from "@visx/text";
import { useChartColors, type ChartColorToken } from "./colors";
import type { Closed } from "./internal/strip";
import type { Omittable } from "../../lib/props";

/** A themed SVG `<text>` (visx `Text`, with word-wrap/anchoring). Named
 * `ChartText` because the library owns `Text` for body copy. Color via
 * `colorToken`, defaulting to the foreground token. A label that sits ON a
 * colored mark (a pie slice, a treemap tile, a stacked segment) names that
 * mark's token as `on` instead — the page foreground is illegible on the light half
 * of the palette. */
export function ChartText({
  colorToken,
  on,
  ...rest
}: Closed<TextProps> &
  Omittable<{
    colorToken?: ChartColorToken;
    /** The token of the fill this label is drawn on — the SAME token the mark
     * was painted with (`useCategoricalColors().tokens`/`token`). Paints the
     * legible foreground each token declares. Wins over `colorToken`. */
    on?: ChartColorToken | null;
  }>) {
  const c = useChartColors();
  // `data-color` binds --c-foreground on THIS element and `fill` reads it there, so
  // the pairing is the browser's, not a contrast calculation of ours — and it
  // re-resolves on a theme flip like every other themed fill.
  if (on) {
    return (
      <VText data-color={on} fill="var(--c-foreground)" {...(rest as TextProps)} />
    );
  }
  return (
    <VText fill={c.resolve(colorToken) ?? c.foreground} {...(rest as TextProps)} />
  );
}

/** Positioning `<g>` with `top`/`left`/`transform` — verbatim visx `Group`,
 *  prefixed because the package barrel already owns `Group` (the base layout
 *  part). Same rule as `ChartGrid`/`ChartTooltip`. */
export { Group as ChartGroup } from "@visx/group";

/**
 * SVG `<defs>` decorations (verbatim visx). Gradients/patterns are referenced by
 * `id` from a mark's fill/stroke; markers cap line ends; clip-paths mask. Build
 * custom gradient stop colors from `useChartColors()` tokens (never raw hex).
 */
export {
  LinearGradient,
  RadialGradient,
  GradientDarkgreenGreen,
  GradientLightgreenGreen,
  GradientOrangeRed,
  GradientPinkBlue,
  GradientPinkRed,
  GradientPurpleOrange,
  GradientPurpleRed,
  GradientPurpleTeal,
  GradientSteelPurple,
  GradientTealBlue,
} from "@visx/gradient";

export {
  Pattern,
  PatternLines,
  PatternCircles,
  PatternWaves,
  PatternHexagons,
  PatternPath,
} from "@visx/pattern";

export {
  Marker,
  MarkerArrow,
  MarkerCross,
  MarkerX,
  MarkerCircle,
  MarkerLine,
} from "@visx/marker";

export { ClipPath, RectClipPath, CircleClipPath } from "@visx/clip-path";
